/**
 * State model for the Voyage Radar sensor row's Refresh button.
 *
 * Every branch here mirrors a guard that already exists in the refresh path
 * (`scene.aisAvailable`, the TOI close window, and the credit wallet). Today
 * those guards are reached by clicking and reading the popup that comes back;
 * this turns them into the button's own state, so the answer is visible BEFORE
 * the click rather than after it.
 *
 * That matters more here than it looks: a refresh spends API credits and
 * nothing gives them back. Pure and separate from the component so the
 * money-adjacent branches can be unit-tested.
 */

import {
  hasVoyageToiFinished,
  voyageCreditsRemaining,
  voyageSensorScene,
  VOYAGE_TOI_ENDED_MESSAGE,
} from '../voyage-credits.model';

/** Why the button is in the state it is. */
export type VoyageRefreshKind =
  /** Wallet covers the action, TOI open — the normal case. */
  | 'ready'
  /** After this spend nothing else is affordable. Worth confirming. */
  | 'last'
  /** A pull is in flight. */
  | 'loading'
  /** Snapshot playback owns the map; refreshing would fight the frame renderer. */
  | 'playback'
  /** The wallet cannot cover this action (empty, or stranded below its cost). */
  | 'exhausted'
  /** TOI plus its seven days of historical access have ended. */
  | 'expired'
  /** TOI has not begun, so the operator has nothing to give yet. */
  | 'not-started';

export interface VoyageRefreshState {
  kind: VoyageRefreshKind;
  /** Button text. The whole message — no count is appended. */
  label: string;
  /**
   * Wallet balance at the time of resolution, or null when unknown.
   *
   * Deliberately NOT rendered on the button: a trailing number is the strongest
   * badge signal there is, and the sensor-row credit pill already carries the
   * balance. Kept here for the tooltip and the last-spend confirmation.
   */
  remaining: number | null;
  /**
   * Credit price of the refresh this state describes — the catalogue entry for
   * the sensor's own target shape (`refresh` for an area, `nearby` for a
   * point), clamped.
   *
   * `nearby` is named for the endpoint it prices, /v1/vessels/nearby, not for
   * this button: the same entry is meant to price a search around a vessel the
   * user selects once plan steps 4-8 build that caller. It is the right price
   * to quote here either way, because a point task's refresh — the gesture
   * this button fires — is the only thing that reaches that endpoint today.
   *
   * Reported rather than left on the input because the host acts on it: the
   * confirmation dialog quotes it and the optimistic wallet debit subtracts it.
   * Re-deriving it there from a constant is exactly how a screen ends up
   * promising one price while the server charges another.
   */
  cost: number;
  tooltip: string;
  disabled: boolean;
  /** Whether acting on this state should confirm first. */
  confirms: boolean;
}

/**
 * Why nothing on this sensor can be acted on yet — the one sentence for it,
 * stated once. The Refresh button's `not-started` tooltip and the sensor
 * checkbox's both read from here, and it matches the popup the Spire/Clyde
 * path raises (`getAISData`) word for word.
 */
export const VOYAGE_TOI_NOT_STARTED_MESSAGE =
  'AIS data is not available until the start of the time of interest (TOI)';

/**
 * The operator has not opened the TOI yet, so there is nothing to give.
 *
 * Strictly `=== false`: `aisAvailable` is absent on shapes the panel has not
 * finished hydrating, and an unknown window must not read as a shut one — the
 * same call `resolveVoyageRefreshState` makes for the `not-started` branch.
 */
export function isVoyageToiNotStarted(sensor: any): boolean {
  return voyageSensorScene(sensor)?.aisAvailable === false;
}

export interface VoyageRefreshInput {
  sensor: any;
  /** The scene the pull would use — `aisAvailable` gates the whole TOI. */
  scene: any;
  /** True while a pull is already in flight. */
  loading: boolean;
  /** True while snapshot playback owns the map. */
  playbackRunning: boolean;
  /** Credit cost of one vessel refresh (from the sensor's action catalogue). */
  cost: number;
  /** Cheapest action in the catalogue — below this a wallet is stranded. */
  minActionCost: number;
}

/**
 * The button's state, resolved in the same order the refresh path checks its
 * guards so the two can never disagree about which message applies.
 *
 * Transient conditions come first — a refresh in flight or playback running are
 * about *now*, and reporting "no credits left" over them would be answering a
 * question the user did not ask yet. The `remaining === null` early-return must
 * stay BEFORE the balance checks: `null < cost` is `true` in JS, so an unknown
 * wallet would otherwise silently read as an empty one.
 */
export function resolveVoyageRefreshState(input: VoyageRefreshInput): VoyageRefreshState {
  const remaining = voyageCreditsRemaining(input.sensor);
  const cost = Number(input.cost) > 0 ? Number(input.cost) : 1;
  const minActionCost = Number(input.minActionCost) > 0 ? Number(input.minActionCost) : cost;

  if (input.loading) {
    return {
      kind: 'loading',
      label: 'Refreshing…',
      remaining,
      cost,
      tooltip: 'Pulling the latest vessel positions',
      disabled: true,
      confirms: false,
    };
  }

  if (input.playbackRunning) {
    return {
      kind: 'playback',
      label: 'Refresh',
      remaining,
      cost,
      tooltip: 'Exit playback to refresh — the map is replaying saved snapshots',
      disabled: true,
      confirms: false,
    };
  }

  // `aisAvailable` is the operator saying the TOI has not opened yet. Checked
  // before the balance: there is nothing to buy, so "none left" would mislead.
  if (input.scene && input.scene.aisAvailable === false) {
    return {
      kind: 'not-started',
      label: 'Not started',
      remaining,
      cost,
      tooltip: VOYAGE_TOI_NOT_STARTED_MESSAGE,
      disabled: true,
      confirms: false,
    };
  }

  // Through the shared predicate, never the raw `timeExpired` flag: that flag
  // is one of three signals for the same fact and the staleness-prone one, and
  // this button must agree with the paid drawers and the refund gate about when
  // the shop shuts. The tooltip used to promise "seven days of access to the
  // historical data" on top of the TOI — no such grace exists anywhere in the
  // code, on either side of the wire.
  if (hasVoyageToiFinished(input.sensor)) {
    return {
      kind: 'expired',
      label: 'TOI ended',
      remaining,
      cost,
      tooltip: VOYAGE_TOI_ENDED_MESSAGE,
      disabled: true,
      confirms: false,
    };
  }

  // Wallet unknown — offer the action rather than refuse it. Blocking on
  // absent data would deny a user something they may well be entitled to, and
  // the server checks the balance again anyway. WeatherEx makes the same call.
  if (remaining === null) {
    return {
      kind: 'ready',
      label: 'Refresh',
      remaining,
      cost,
      tooltip: 'Pull the latest vessel positions for this target',
      disabled: false,
      confirms: false,
    };
  }

  if (remaining <= 0) {
    return {
      kind: 'exhausted',
      label: 'No credits left',
      remaining,
      cost,
      tooltip: 'Your credits are spent or refunded, please Re-task to buy more.',
      disabled: true,
      confirms: false,
    };
  }

  // A non-zero balance the action cannot cover — stranded, not healthy.
  if (remaining < cost) {
    return {
      kind: 'exhausted',
      label: 'Not enough credits',
      remaining,
      cost,
      tooltip:
        `A vessel refresh costs ${cost} credits and you have ${remaining} left. ` +
        'Re-task to buy more.',
      disabled: true,
      confirms: false,
    };
  }

  // The spend after which nothing is affordable is the only one whose loss
  // cannot be worked around, so it is the only one worth interrupting for.
  // Generalises the old "last credit" rule: with cost 2 and min 2 this fires
  // at a balance of 2 or 3. Confirming on every refresh would nag.
  if (remaining - cost < minActionCost) {
    return {
      kind: 'last',
      label: 'Refresh',
      remaining,
      cost,
      tooltip: 'These are the last credits you can spend on this sensor',
      disabled: false,
      confirms: true,
    };
  }

  return {
    kind: 'ready',
    label: 'Refresh',
    remaining,
    cost,
    tooltip: `Pull the latest vessel positions · uses ${cost} of ${remaining} credits`,
    disabled: false,
    confirms: false,
  };
}
